import AbstractPreloader from '~/core/AppInit/AbstractPreloader';
import { armoryState, csrf, settings } from '~/utils/settings';
import { logError, logInfo } from '~/utils/logging';
import wowsEntities from '@wg/wows-entities';
import { get } from '~/utils/ajax';
import { prepareSubCategories } from '~/utils/category';
import Account from '~/account/Account';
import store from '~/Store';
import { getStepsFromGuide } from '~/components/WelcomePage/steps';
import { preloadCategoryBackgrounds } from '~/utils/preCacheImage';
import { setFiltersInfo } from '~/settings/filtersMap';
import { WarmingUpCollections } from '@wg/wows-entities/const';
import { isInGameBrowser } from '~/utils/utils';
import { getAvailableGifts } from '~/api/gifts';
import { getClientSettings } from '@wg/web2clientapi/browser/getClientSettings';
import { isNeedToShowWelcomeGiftScreen } from '~/utils/gift';
import dwhExport from '~/api/dwhExport';
import { DWH_EVENTS } from '~/const';
import { accountActions } from '~/Store/accountSlice';
import { appActions } from '~/Store/appSlice';
import { BUNDLE_DESCRIPTIONS_TYPE } from '~/types/bundle';

export default class ArmoryPreloader extends AbstractPreloader {
    constructor(
        private user: IAccount = window.metashop.state?.account,
        private version: string = null,
        private bundles: IBundleList = window.metashop.state.content?.bundles,
    ) {
        super();
    }

    async load(): Promise<any> {
        // todo: add retries
        csrf().catch(() => {
            logInfo('request to csrf was failed');
        });

        try {
            await this.initWowsEntities().catch((e) => {
                logError('Error during Wows entities init', e);
            });
            const gameVersion = wowsEntities.settings.gameVersion;
            if (!gameVersion) {
                throw 'No game version';
            }
            this.version = gameVersion;
            window.metashop.settings.version = this.version;
            await this.prepareBundles();
        } catch (e) {
            this.initApp({
                data: this.bundles,
                vehicleTypes: [],
                nations: [],
                currencies: [],
            });
            logError('vortex is unavailable');
        }
        return Promise.resolve(1);
    }

    private async initWowsEntities() {
        return wowsEntities
            .init({
                vortexBaseUrl: settings.urls.vortex,
                languageCode: settings.languageCode,
            })
            .then(() => {
                wowsEntities.warmingUp([
                    WarmingUpCollections.vehicles,
                    WarmingUpCollections.crews,
                    WarmingUpCollections.lootbox,
                    WarmingUpCollections.items,
                    WarmingUpCollections.rarity,
                    WarmingUpCollections.nations,
                    WarmingUpCollections.currencies,
                    WarmingUpCollections.vehicleTypes,
                    WarmingUpCollections.dogTagComponents,
                    WarmingUpCollections.features,
                ]);
            });
    }

    private waitForSeconds(n: number) {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(1);
            }, n * 1000);
        });
    }

    private async prepareBundles() {
        if (wowsEntities.getWarmingUpHelper().isInProgress()) {
            await this.waitForSeconds(0.1);
            await this.prepareBundles();
            return;
        }

        setFiltersInfo().then(() => {
            const response = {
                data: this.bundles,
                vehicleTypes: Object.values(wowsEntities.getWarmingUpCache().get(WarmingUpCollections.vehicleTypes).data),
                nations: Object.values(wowsEntities.getWarmingUpCache().get(WarmingUpCollections.nations).data),
                currencies: Object.values(wowsEntities.getWarmingUpCache().get(WarmingUpCollections.currencies).data),
            };

            this.initApp(response);
        });

        return Promise.resolve(1);
    }

    private async initApp(response: any) {
        prepareSubCategories();

        if (this.user) {
            store.dispatch(accountActions.initAccount());
            Account.loadAccountInventoryFromVrtx();
        }

        store.dispatch(
            appActions.init({
                response,
                deniedBundlesByUniqueItems: Account.getDeniedBundlesByUniqueItems(),
                purchasedLimitedBundles: Account.getPurchasedLimitedBundles(),
            }),
        );

        this.postInitialisation();

        this.getUserGraphicsSettings();
    }

    private postInitialisation() {
        const { bundles, categories } = store.getState().app;
        if (this.user) {
            store.dispatch(accountActions.initGuideSteps(getStepsFromGuide()));
            store.dispatch(accountActions.setCoupons({ coupons: armoryState.content?.coupons, categories }));
        }
        if (bundles && categories && !!Account.getAccount()?.id) {
            store.dispatch(accountActions.setNotifications({ notifications: armoryState.content.notifications as INotification[], categories, bundles }));
        }
        preloadCategoryBackgrounds();

        this.loadBundleDescription();

        this.checkGifts();
    }

    private async loadBundleDescription() {
        const response: { data: Record<number, BUNDLE_DESCRIPTIONS_TYPE> } = await get(settings.urls.getBundleDescriptions);
        if (!response || !response.data) {
            return;
        }
        store.dispatch(appActions.setBundlesDescription(response.data));
    }

    async checkGifts() {
        const response: IRequestGift = await getAvailableGifts();
        const gifts = response.data;

        if (!gifts.length) {
            return;
        }

        if (Account.getAccount()?.id) {
            const availableGifts = gifts.filter((gift) => {
                return !gift.isObtained;
            });
            store.dispatch(accountActions.setGifts(availableGifts));
        } else if (isNeedToShowWelcomeGiftScreen(gifts?.[0]?.name)) {
            store.dispatch(accountActions.setGifts(gifts));
        }
    }

    async getUserGraphicsSettings() {
        if (!isInGameBrowser) {
            return;
        }

        try {
            const response = await getClientSettings();
            response.data && store.dispatch(accountActions.setClientSettings(response.data));
            dwhExport.send(DWH_EVENTS.USER_GRAPHICS_PRESET, {
                preset: response.data.preset,
                web_browser_hardware_acceleration: response.data.settings.web_browser_hardware_acceleration,
            });
        } catch (e) {
            logInfo('Graphics settings was failed');
        }
    }
}
